import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import App from "./App";
import "./i18n";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2E7D8A", // Healthcare teal - professional and calming
      light: "#5BA3B0",
      dark: "#1A5A66",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#E91E63", // Soft pink - feminine and modern
      light: "#F06292",
      dark: "#C2185B",
      contrastText: "#ffffff",
    },
    background: {
      default: "#FAFBFC", // Very light blue-gray
      paper: "#ffffff",
    },
    success: {
      main: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
    },
    warning: {
      main: "#FF9800",
      light: "#FFB74D",
      dark: "#F57C00",
    },
    error: {
      main: "#F44336",
      light: "#E57373",
      dark: "#D32F2F",
    },
    info: {
      main: "#2196F3",
      light: "#64B5F6",
      dark: "#1976D2",
    },
    text: {
      primary: "#2C3E50", // Dark blue-gray for better readability
      secondary: "#6C7B7F",
    },
    grey: {
      50: "#F8F9FA",
      100: "#F1F3F4",
      200: "#E8EAED",
      300: "#DADCE0",
      400: "#BDC1C6",
      500: "#9AA0A6",
      600: "#80868B",
      700: "#5F6368",
      800: "#3C4043",
      900: "#202124",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 500,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 500,
      textTransform: "none",
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(0, 0, 0, 0.05)",
    "0px 4px 8px rgba(0, 0, 0, 0.08)",
    "0px 8px 16px rgba(0, 0, 0, 0.1)",
    "0px 12px 24px rgba(0, 0, 0, 0.12)",
    "0px 16px 32px rgba(0, 0, 0, 0.14)",
    "0px 20px 40px rgba(0, 0, 0, 0.16)",
    "0px 24px 48px rgba(0, 0, 0, 0.18)",
    "0px 28px 56px rgba(0, 0, 0, 0.20)",
    "0px 32px 64px rgba(0, 0, 0, 0.22)",
    "0px 36px 72px rgba(0, 0, 0, 0.24)",
    "0px 40px 80px rgba(0, 0, 0, 0.26)",
    "0px 44px 88px rgba(0, 0, 0, 0.28)",
    "0px 48px 96px rgba(0, 0, 0, 0.30)",
    "0px 52px 104px rgba(0, 0, 0, 0.32)",
    "0px 56px 112px rgba(0, 0, 0, 0.34)",
    "0px 60px 120px rgba(0, 0, 0, 0.36)",
    "0px 64px 128px rgba(0, 0, 0, 0.38)",
    "0px 68px 136px rgba(0, 0, 0, 0.40)",
    "0px 72px 144px rgba(0, 0, 0, 0.42)",
    "0px 76px 152px rgba(0, 0, 0, 0.44)",
    "0px 80px 160px rgba(0, 0, 0, 0.46)",
    "0px 84px 168px rgba(0, 0, 0, 0.48)",
    "0px 88px 176px rgba(0, 0, 0, 0.50)",
    "0px 92px 184px rgba(0, 0, 0, 0.52)",
  ],
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          borderRadius: 16,
          border: "1px solid rgba(0, 0, 0, 0.05)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-2px)",
          },
          "@media (max-width: 600px)": {
            borderRadius: 12,
            "&:hover": {
              transform: "none",
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: "10px 24px",
          fontWeight: 500,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          },
          "@media (max-width: 600px)": {
            padding: "12px 20px",
            fontSize: "0.9rem",
          },
        },
        contained: {
          background: "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #1A5A66 0%, #2E7D8A 100%)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
          borderRight: "1px solid rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "4px 8px",
          "&.Mui-selected": {
            background: "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
            color: "white",
            "&:hover": {
              background: "linear-gradient(135deg, #1A5A66 0%, #2E7D8A 100%)",
            },
          },
          "&:hover": {
            background: "rgba(46, 125, 138, 0.08)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#2E7D8A",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#2E7D8A",
              borderWidth: 2,
            },
            "@media (max-width: 600px)": {
              borderRadius: 8,
              fontSize: "16px", // Prevents zoom on iOS
            },
          },
          "@media (max-width: 600px)": {
            "& .MuiInputLabel-root": {
              fontSize: "16px",
            },
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <App />
        </LocalizationProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
